import React, { useState } from 'react';

function ResearchFundamentalsTable({ data, onTickerClick }) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const formatCurrency = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `$${Number(value).toFixed(2)}`;
  };

  const formatPercent = (value) => {
    if (value == null || isNaN(value)) return '--';
    const num = Number(value) * 100;
    const color = num >= 0 ? 'text-green-400' : 'text-red-400';
    return <span className={color}>{num > 0 ? '+' : ''}{num.toFixed(2)}%</span>;
  };

  const formatNumber = (value) => {
    if (value == null || isNaN(value)) return '--';
    return Number(value).toFixed(2);
  };

  const formatPlainPercent = (value) => {
    if (value == null || isNaN(value)) return '--';
    return `${(Number(value) * 100).toFixed(2)}%`;
  };

  const formatLargeNumber = (value) => {
    if (value == null || isNaN(value)) return '--';
    const num = Number(value);
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
  };

  const formatDateTime = (value) => {
    if (!value) return '--';
    try {
      const date = new Date(value);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return String(value);
    }
  };

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const comparison = aVal > bVal ? 1 : -1;
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const SortableHeader = ({ field, label, align = 'right' }) => (
    <th
      className={`text-${align} py-2 px-3 cursor-pointer hover:opacity-80 whitespace-nowrap`}
      style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
      onClick={() => handleSort(field)}
    >
      {label}
      {sortField === field && (
        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
      )}
    </th>
  );

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No fundamentals data available. Run an analysis from the Analysis Input tab.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-max">
        <thead>
          {/* Group Headers */}
          <tr style={{ backgroundColor: '#1a1a1a' }}>
            <th colSpan={3} className="text-center py-1 px-3 text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}></th>
            <th colSpan={4} className="text-center py-1 px-3 text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700 border-l border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>Highs</th>
            <th colSpan={3} className="text-center py-1 px-3 text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700 border-l border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>Range</th>
            <th colSpan={3} className="text-center py-1 px-3 text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700 border-l border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>5-Year Range</th>
            <th colSpan={7} className="text-center py-1 px-3 text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700 border-l border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>Valuation</th>
            <th colSpan={4} className="text-center py-1 px-3 text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700 border-l border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>Profitability</th>
            <th colSpan={4} className="text-center py-1 px-3 text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700 border-l border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>Technicals</th>
            <th colSpan={1} className="text-center py-1 px-3 text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700 border-l border-gray-700" style={{ backgroundColor: '#1a1a1a' }}></th>
            <th colSpan={1} className="text-center py-1 px-3 text-gray-500 text-[10px] uppercase tracking-wider border-b border-gray-700 border-l border-gray-700" style={{ backgroundColor: '#1a1a1a' }}></th>
          </tr>
          {/* Column Headers */}
          <tr className="border-b border-gray-700" style={{ backgroundColor: '#1a1a1a' }}>
            {/* Ticker (sticky) */}
            <th
              className="text-left py-2 px-3 sticky left-0 z-10 cursor-pointer hover:opacity-80"
              style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
              onClick={() => handleSort('Ticker')}
            >
              Ticker {sortField === 'Ticker' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <SortableHeader field="Current Price" label="Price" />
            <SortableHeader field="Market Cap" label="Mkt Cap" />

            {/* Highs */}
            <SortableHeader field="ATH" label="ATH" />
            <SortableHeader field="ATH %Chg" label="ATH %Chg" />
            <SortableHeader field="52 Wk High" label="52Wk Hi" />
            <SortableHeader field="6 Mo High" label="6Mo Hi" />

            {/* Range */}
            <SortableHeader field="6 Mo High %Chg" label="6Mo Hi %Chg" />
            <SortableHeader field="6 Mo Low" label="6Mo Lo" />
            <SortableHeader field="6 Mo Low %Chg" label="6Mo Lo %Chg" />

            {/* 5-Year Range */}
            <SortableHeader field="5 Yr High" label="5Yr Hi" />
            <SortableHeader field="5 Yr Low" label="5Yr Lo" />
            <SortableHeader field="52 Wk Low" label="52Wk Lo" />

            {/* Valuation */}
            <SortableHeader field="Current PE" label="P/E" />
            <SortableHeader field="Forward PE" label="Fwd P/E" />
            <SortableHeader field="PEG Ratio" label="PEG" />
            <SortableHeader field="5 Yr Avg PE" label="5Yr Avg P/E" />
            <SortableHeader field="5 Yr Low PE" label="5Yr Lo P/E" />
            <SortableHeader field="5 Yr High PE" label="5Yr Hi P/E" />
            <SortableHeader field="Dividend Yield" label="Div Yield" />

            {/* Profitability */}
            <SortableHeader field="EPS (TTM)" label="EPS" />
            <SortableHeader field="Fwd EPS" label="Fwd EPS" />
            <SortableHeader field="Profit Margin" label="Profit Mgn" />
            <SortableHeader field="ROE" label="ROE" />

            {/* Technicals */}
            <SortableHeader field="Beta" label="Beta" />
            <SortableHeader field="MACD" label="MACD" />
            <SortableHeader field="MACD Signal" label="Signal" />
            <SortableHeader field="RSI" label="RSI" />

            {/* Target */}
            <SortableHeader field="Wall St Target" label="Target" />

            {/* Timestamp */}
            <th
              className="text-center py-2 px-3 whitespace-nowrap"
              style={{ backgroundColor: '#1a1a1a', color: '#ffffff' }}
            >
              Live Price Time
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, index) => (
            <tr key={index} className="border-b border-gray-800 hover:bg-[rgba(33,138,255,0.15)] transition-colors">
              {/* Ticker (sticky) */}
              <td className="py-2 px-3 font-medium text-white sticky left-0 z-10 bg-gray-800">
                <span
                  className="cursor-pointer text-blue-400 hover:text-blue-300 hover:underline"
                  onClick={() => onTickerClick && onTickerClick(row['Ticker'])}
                >
                  {row['Ticker']}
                </span>
              </td>
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['Current Price'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatLargeNumber(row['Market Cap'])}</td>

              {/* Highs */}
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['ATH'])}</td>
              <td className="py-2 px-3 text-right">{formatPercent(row['ATH %Chg'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['52 Wk High'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['6 Mo High'])}</td>

              {/* Range */}
              <td className="py-2 px-3 text-right">{formatPercent(row['6 Mo High %Chg'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['6 Mo Low'])}</td>
              <td className="py-2 px-3 text-right">{formatPercent(row['6 Mo Low %Chg'])}</td>

              {/* 5-Year Range */}
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['5 Yr High'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['5 Yr Low'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['52 Wk Low'])}</td>

              {/* Valuation */}
              <td className="py-2 px-3 text-right text-white">{formatNumber(row['Current PE'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatNumber(row['Forward PE'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatNumber(row['PEG Ratio'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatNumber(row['5 Yr Avg PE'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatNumber(row['5 Yr Low PE'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatNumber(row['5 Yr High PE'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatPlainPercent(row['Dividend Yield'])}</td>

              {/* Profitability */}
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['EPS (TTM)'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['Fwd EPS'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatPlainPercent(row['Profit Margin'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatPlainPercent(row['ROE'])}</td>

              {/* Technicals */}
              <td className="py-2 px-3 text-right text-white">{formatNumber(row['Beta'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatNumber(row['MACD'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatNumber(row['MACD Signal'])}</td>
              <td className="py-2 px-3 text-right text-white">{formatNumber(row['RSI'])}</td>

              {/* Target */}
              <td className="py-2 px-3 text-right text-white">{formatCurrency(row['Wall St Target'])}</td>

              {/* Timestamp */}
              <td className="py-2 px-3 text-center text-gray-300 text-xs">
                {formatDateTime(row['Live Price Time'])}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-4 text-sm text-gray-400">
        Showing {sortedData.length} {sortedData.length === 1 ? 'stock' : 'stocks'} analyzed
      </div>
    </div>
  );
}

export default ResearchFundamentalsTable;
